
/**
 * @module api/requests/users
 */
import API_ENDPOINTS from "./apiEndpoints";

/**
 * Remove user from group in tenant.
 * @memberof module:api/requests/groups/users
 * @function
 * @name AddUserToGroup
 * @param {string} accessToken - Bearer token for authorization.
 * @param {Object} payload - User details object to remove.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const leaveTenant = async (tenantId,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.self.leaveTenant(tenantId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      }
    }
  );

  return response;
};

/**
 * Fetch all users details for a tenant.
 * @memberof module:api/requests/users
 * @function
 * @name getAllUsersDetails
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const getAllUsersDetails = async (tenantId,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.users.getAll(tenantId)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );

  return response;
};

export const recoverPassword = async (emailAddress) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.recoverPassword}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({email_address:emailAddress}),
    }
  );

  return response;
};


/**
 * Verify password recovery token.
 * @memberof module:api/requests/users
 * @function verifyRegisterToken
 * @name verifyRecoverToken
 * @param {string} userId - User ID.
 * @param {string} recoverToken - Recovery token to verify.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const verifyRecoverToken = async (recoverToken) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.verifyRecoverToken}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: recoverToken ? `Bearer ${recoverToken}` : undefined, },
    }
  );
  return response;
};

/**
 * Change the password of a user
 * @memberof module:api/requests/roles
 * @function
 * @name getAvailableRoles
 * @param {string} userId - User ID.
  * @param {Object} payload - Password details object.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const changePassword = async (password,recoverToken) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.recoverPassword}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json",Authorization: recoverToken ? `Bearer ${recoverToken}` : undefined,},
      body: JSON.stringify({password:password}),
    }
  );
  return response;
};


/**
 * Fetch the profile of the currently authenticated user.
 * @memberof module:api/requests/users
 * @function
 * @name getUserProfile
 * @param {string} accessToken - Bearer token for authorization.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const getUserProfile = async (tenantId, userId, accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.users.getProfile(tenantId, userId)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );

  return response;
};

/**
 * Update the profile of the currently authenticated user.
 * @memberof module:api/requests/users
 * @function
 * @name getUserProfile
 * @param {string} accessToken - Bearer token for authorization.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const updateUserProfile = async (payload, accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.self.updateProfile}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(payload),
    }
  );

  return response;
};

/**
 * Enable or disable email notifications for current user
 * @memberof module:api/requests/users
 * @function
 * @name enableNotifications
 * @param {boolean} isEnabled - flag used to enable or disable email notifications .
 * @param {string} accessToken - Bearer token for authorization.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const enableNotifications = async (isEnabled, accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.self.notificationsEnabled(isEnabled)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );

  return response;
};

/**
 * Update the profile of the currently authenticated user.
 * @memberof module:api/requests/users
 * @function
 * @name getUserProfile
 * @param {string} accessToken - Bearer token for authorization.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const updateUserPassword = async (payload, accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.self.updatePassword}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(payload),
    }
  );

  return response;
};

/**
 * Add user in group in tenant.
 * @memberof module:api/requests/groups/users
 * @function
 * @name AddUserToGroup
 * @param {string} accessToken - Bearer token for authorization.
 * @param {Object} payload - User details object to add.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const addUserToGroup = async (tenantId,groupId, payload,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.addUser(tenantId,groupId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(payload),
    }
  );

  return response;
};

/**
 * Remove user from group in tenant.
 * @memberof module:api/requests/groups/users
 * @function
 * @name AddUserToGroup
 * @param {string} accessToken - Bearer token for authorization.
 * @param {Object} payload - User details object to remove.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const removeUserFromGroup = async (tenantId,groupId, payload,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.deleteUser(tenantId,groupId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(payload),
    }
  );

  return response;
};

/**
 * Change user's role in group in tenant.
 * @memberof module:api/requests/groups/users
 * @function
 * @name AddUserToGroup
 * @param {string} accessToken - Bearer token for authorization.
 * @param {Object} payload - User details object to remove.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const updateUserInGroup = async (tenantId,groupId,userId, payload,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.updateUserRole(tenantId,groupId,userId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(payload),
    }
  );

  return response;
};


/**
 * Update user details.
 * @memberof module:api/requests/users
 * @function
 * @name updateUsersDetails
 * @param {string} accessToken - Bearer token for authorization.
 * @param {Object} userDetails - User details object to update.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const updateUsersDetails = async (tenantId,userId, userDetails,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.users.updateProfile(tenantId,userId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(userDetails),
    }
  );

  return response;
};

/**
 * Delete a user by their email address.
 * @memberof module:api/requests/users
 * @function
 * @name deleteUserById
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} userId - Id of the user to delete.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const deleteUserById = async (tenantId, userId,accessToken) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.tenants.users.deleteById(tenantId, userId)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );

  return response;
};

/**
 * Update the default tenant for the authenticated user.
 * @memberof module:api/requests/users
 * @function
 * @name updateDefaultTenant
 * @param {string} accessToken - Bearer token for authorization.
 * @param {Object} defaultTenant - Object representing the new default tenant.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const updateDefaultTenant = async (tenantId, userId,defaultTenant,accessToken, ) => {

  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.users.updateDefaultTenant(tenantId, userId)}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify({tenantId:defaultTenant}),
    }
  );


  return response;
};

/**
 * Send a registration invitation to a user.
 * @memberof module:api/requests/users
 * @function
 * @name sendRegisterInvitation
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @param {Object} userDetails - User details for invitation.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const sendRegisterInvitation = async (tenantId, userDetails,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.users.postInvitation(tenantId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
      body: JSON.stringify(userDetails),
    }
  );

  return response;
};

/**
 * Register a new user for a tenant.
 * @memberof module:api/requests/users
 * @function
 * @name registerUser
 * @param {string} tenantId - Tenant ID.
 * @param {Object} userDetails - User registration details.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const registerUser = async (tenantId,registerToken, userDetails) => {

  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.tenants.users.postRegister(tenantId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization":`Bearer ${registerToken}` },
      body: JSON.stringify(userDetails),
    }
  );
  return response;
};


/**
 * Fetch groups for a tenant.
 * @memberof module:api/requests/groups
 * @function
 * @name getGroups
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const getGroups = async (tenantId, accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.getAll(tenantId)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );

  return response;
};


/**
 * Fetch detailed groups information for a tenant with optional limit.
 * @memberof module:api/requests/groups
 * @function
 * @name getGroupsDetails
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @param {number} [limit=0] - Maximum number of groups to fetch (0 for no limit).
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const getGroupsDetails = async (tenantId, limit = 0, accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.getDetails(tenantId, limit)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );

  return response;
};

/**
 * Register a new owner.
 * @memberof module:api/requests/users
 * @function
 * @name registerUser
 * @param {string} tenantId - Tenant ID.
 * @param {Object} ownerDetails - User registration details.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const registerOwner = async (tenantId,registerToken,ownerDetails) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.tenants.owners.postRegister(tenantId)}`,
    {
      method: "POST",
       headers: { "Content-Type": "application/json", "Authorization":`Bearer ${registerToken}` },
      body: JSON.stringify(ownerDetails),
    }
  );
  return response;
};


/**
 * Create a new group for a tenant.
 * @memberof module:api/requests/groups
 * @function
 * @name createGroup
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @param {Object} groupDetailsPayload - Group details object.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const createGroup = async (tenantId,groupDetailsPayload,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.create(tenantId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      },
      body: JSON.stringify(groupDetailsPayload),
    }
  );
  return response;
};

/**
 * Delete a group by name in a tenant.
 * @memberof module:api/requests/groups
 * @function
 * @name deleteGroup
 * @param {string} accessToken - Bearer token for authorization.
 * @param {string} tenantId - Tenant ID.
 * @param {string} groupName - Name of the group to delete.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const deleteGroup = async (tenantId,groupName,accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.tenants.groups.delete(tenantId,groupName)}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
        "X-Tenant-Id": tenantId,
      }
    }
  );
  return response;
};

/**
 * Verify registration token.
 * @memberof module:api/requests/users
 * @function verifyRegisterToken
 * @name verifyRegisterToken
 * @param {string} tenantId - Tenant ID.
 * @param {string} registerToken - Registration token to verify.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const verifyOwnerRegisterToken = async (tenantId,registerToken) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.tenants.owners.postRegisterToken(tenantId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Tenant-Id":tenantId, "Authorization":`Bearer ${registerToken}` },
    }
  );
  return response;
};

/**
 * Verify registration token.
 * @memberof module:api/requests/users
 * @function verifyRegisterToken
 * @name verifyRegisterToken
 * @param {string} tenantId - Tenant ID.
 * @param {string} registerToken - Registration token to verify.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const verifyUserRegisterToken = async (tenantId,registerToken) => {
  const response = await fetch(
    `${
      API_ENDPOINTS.usersApi.baseUrl
    }${API_ENDPOINTS.usersApi.endpoints.tenants.users.postRegisterToken(tenantId)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Tenant-Id":tenantId, "Authorization":`Bearer ${registerToken}` },
    }
  );
  return response;
};

/**
 * Fetch available roles.
 * @memberof module:api/requests/roles
 * @function
 * @name getAvailableRoles
 * @param {string} accessToken - Bearer token for authorization.
 * @returns {Promise<Response>} Fetch API Response object.
 */
export const getAvailableRoles = async (accessToken) => {
  const response = await fetch(
    `${API_ENDPOINTS.usersApi.baseUrl}${API_ENDPOINTS.usersApi.endpoints.getRoles}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: accessToken ? `Bearer ${accessToken}` : undefined,
      },
    }
  );
  return response;
};